'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import ComingSoonPage from './ComingSoonPage'

interface MaintenanceWrapperProps {
  children: React.ReactNode
}

/**
 * Client-side wrapper that checks maintenance mode and conditionally shows Coming Soon page
 * Only affects public routes, allows admin and login pages to work normally
 */
export default function MaintenanceWrapper({ children }: MaintenanceWrapperProps) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const pathname = usePathname()

  // Paths that should bypass maintenance mode
  const exemptPaths = ['/admin', '/login']
  const isExemptPath = exemptPaths.some(path => pathname.startsWith(path))

  useEffect(() => {
    const checkMaintenanceMode = () => {
      try {
        const maintenanceMode = localStorage.getItem('maintenance-mode')
        setIsMaintenanceMode(maintenanceMode === 'true')
      } catch (error) {
        console.error('Failed to check maintenance mode:', error)
        setIsMaintenanceMode(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkMaintenanceMode()

    // Listen for storage changes (when maintenance mode is toggled in admin)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'maintenance-mode') {
        setIsMaintenanceMode(event.newValue === 'true')
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  // Show loading state briefly while checking maintenance mode
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-luxury-gradient">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg text-luxury-gold mb-4"></div>
          <p className="text-white/80">Loading...</p>
        </div>
      </div>
    )
  }

  // If maintenance mode is enabled and this isn't an exempt path, show coming soon page
  if (isMaintenanceMode && !isExemptPath) {
    return <ComingSoonPage />
  }

  // Otherwise, render the normal content
  return <>{children}</>
}